import { useState, FormEvent, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { verify2FA, ApiException } from '../api/client';
import { useAuth } from '../App';
import { sichereRuecksprungAdresse } from '../lib/weiterleitung';
import { Karte, Notiz } from '../components/leitstand';

/**
 * Zwei-Faktor-Abfrage nach der Anmeldung.
 *
 * Laeuft noch vor der fertigen Anmeldung — kein Benutzer, keine Navigation.
 * Darum die mittige Karte im Leitstand-Stil statt des vollen Rahmens.
 *
 * Sechs einzelne Felder mit Sprung ins naechste und Einfuegen aus der
 * Zwischenablage: Wer den Code aus der App abtippt, soll nicht zaehlen muessen.
 */

export default function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();

  // returnTo aus URL Query Parameter
  const returnTo = sichereRuecksprungAdresse(searchParams.get('returnTo'));
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Nur Zahlen erlauben
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus nächstes Feld
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit wenn vollständig
    if (newCode.every((d) => d) && newCode.join('').length === 6) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Backspace: vorheriges Feld fokussieren
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Bitte alle sechs Stellen des Codes eingeben.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await verify2FA(codeToVerify);
      // WICHTIG: AuthContext refreshen bevor Navigation
      await refresh();
      // Zu returnTo navigieren (oder /dashboard als Fallback)
      navigate(returnTo);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(
          err.data.error || 'Der Code stimmt nicht. Bitte den aktuellen Code aus der App eingeben.',
        );
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
      // Code zurücksetzen
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <Karte titel="Zwei-Faktor-Anmeldung">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-akzent" />
            </div>
            <p className="text-tinte-2">
              Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-md border border-stoer bg-flaeche-2">
              <p className="text-stoer text-sm">{error}</p>
            </div>
          )}

          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-6"
          >
            <div className="flex justify-center gap-3" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold font-zahl tabular-nums border-2 border-linie-2 bg-flaeche-2 text-tinte rounded-lg focus:border-akzent outline-none transition-all disabled:opacity-50"
                  disabled={loading}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || code.some((d) => !d)}
              className="w-full min-h-[48px] rounded-lg border border-akzent bg-akzent px-6 py-3 font-medium text-akzent-satz transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Wird geprüft...
                </>
              ) : (
                'Bestätigen'
              )}
            </button>
          </form>

          <div className="mt-6">
            <Notiz titel="Wo steht der Code?">
              Öffnen Sie Ihre Authenticator-App (z.B. Google Authenticator) und geben Sie den dort
              angezeigten Code ein. Er wechselt alle 30 Sekunden.
            </Notiz>
          </div>
        </Karte>
      </div>
    </div>
  );
}
